import React, { useEffect, useMemo, useState } from 'react'
import { SquareButton } from '../../../styles/ButtonStyles'
import { Button, Drawer, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import { ArrowBack, AttachMoney } from '@mui/icons-material'
import { t } from 'i18next'
import CostContent from './CostContent'
import { User } from '../../../store/types'
import DiscardChangesDialog from '../../Dialog/DiscardChangesDialog'
import { useForm } from 'react-hook-form'
import { CostFormValues, defaultCostFormValues } from '../../../store/types/form/CostFormValues'
import Cost from '../../../store/types/models/Cost'
import { useAppSelector } from '../../../store/redux/hooks'
import { getSelectedDate } from '../../../store/redux/reducers/monthPickerSlice'
import dayjs from 'dayjs'

type CostSidebarProps = {
  resize: boolean
  date: string
  employeeId: string | undefined
  selectedEmployee: User | null
  fullWidth?: boolean
}

const CostSidebar = ({ resize, date, employeeId, selectedEmployee, fullWidth = false }: CostSidebarProps) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleOpen = () => setOpen(true)

  const handleClose = () => {
    if (isChangePresent) setIsCancelDialogOpen(true)
    else {
      handleResetForm()
      setOpen(false)
    }
  }

  const selectedDate = useAppSelector(getSelectedDate)

  const defaultValues = useMemo(() => {
    return {
      ...defaultCostFormValues,
      startDate: dayjs(selectedDate).startOf('month').utc(true),
      endDate: dayjs(selectedDate).endOf('month').utc(true),
    }
  }, [selectedDate])

  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const { handleSubmit, control, reset, getValues, formState, watch } = useForm<CostFormValues>({
    defaultValues: defaultValues,
    mode: 'onChange',
  })

  const [costToEdit, setCostToEdit] = useState<Cost | null>(null)
  const [isViewForm, setIsViewForm] = useState<boolean>(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false)
  const [isCancelFormButtonClick, setIsCancelFormButtonClick] = useState<boolean>(false)
  const [isChangePresent, setIsChangePresent] = useState<boolean>(false)

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  useEffect(() => {
    if (isCancelFormButtonClick) setIsCancelDialogOpen(true)
  }, [isCancelFormButtonClick])

  const handleResetForm = () => {
    reset(defaultValues)
    setIsViewForm(false)
    setCostToEdit(null)
    setIsCancelFormButtonClick(false)
  }

  const handleDialogReset = () => {
    if (!isCancelFormButtonClick) setOpen(false)
    handleResetForm()
  }

  return (
    <>
      {resize ? (
        <Tooltip title={t('page.worklog.costs')} placement="bottom">
          <SquareButton onClick={handleOpen} color="secondary" aria-label={t('page.worklog.costs')}>
            <AttachMoney />
          </SquareButton>
        </Tooltip>
      ) : (
        <Button
          variant={'contained'}
          startIcon={<AttachMoney />}
          color="secondary"
          size="medium"
          onClick={handleOpen}
          fullWidth={fullWidth}
        >
          {t('page.worklog.costs')}
        </Button>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { bgcolor: 'background.main', overflowX: 'hidden' }, elevation: 0 }}
      >
        <Stack px={isDownSm ? 3 : 6} pt={3} pb={3} width={isDownSm ? '350px' : '570px'}>
          <Button
            onClick={handleClose}
            variant="text"
            color="secondary"
            startIcon={<ArrowBack />}
            size="small"
            sx={{ position: 'absolute', ml: -3 }}
            aria-label={t('components.infoSidebar.return')}
          >
            {t('components.infoSidebar.return')}
          </Button>
          <CostContent
            date={date}
            employeeId={employeeId}
            selectedEmployee={selectedEmployee}
            setIsChangePresent={setIsChangePresent}
            setIsCancelFormButtonClick={setIsCancelFormButtonClick}
            control={control}
            handleSubmit={handleSubmit}
            formState={formState}
            getValues={getValues}
            reset={reset}
            costToEdit={costToEdit}
            setCostToEdit={setCostToEdit}
            isViewForm={isViewForm}
            setIsViewForm={setIsViewForm}
            handleResetForm={handleResetForm}
            watch={watch}
            selectedDate={selectedDate}
          />
        </Stack>
      </Drawer>
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={() => true}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
        disableBlocker={true}
        resetForm={handleDialogReset}
        actionOnClose={() => setIsCancelFormButtonClick(false)}
      />
    </>
  )
}

export default CostSidebar
